import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { PhoneMockup } from './PhoneMockup';
import { BoxIcon } from 'lucide-react';
interface FeatureSectionProps {
  badge: string;
  icon: BoxIcon;
  title: string;
  highlight: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
}
export function FeatureSection({
  badge,
  icon: Icon,
  title,
  highlight,
  description,
  imageUrl,
  imageAlt,
  reverse = false
}: FeatureSectionProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="relative py-20 px-6 overflow-hidden" data-id="element-30">
      {/* Background decorations */}
      <div className={`absolute ${reverse ? 'left-0' : 'right-0'} top-1/4 w-64 h-64 bg-orange-200 rounded-full blur-3xl opacity-20`} data-id="element-31"></div>

      {/* Dot grid decoration */}
      <div className={`absolute ${reverse ? 'right-20' : 'left-20'} top-20 grid grid-cols-3 gap-2 opacity-30`} data-id="element-32">
        {[...Array(9)].map((_, i) => <div key={i} className="w-1.5 h-1.5 bg-orange-400 rounded-full" data-id="element-33"></div>)}
      </div>

      <div className="relative max-w-7xl mx-auto" data-id="element-34">
        <div className={`grid lg:grid-cols-2 gap-16 items-center ${reverse ? 'lg:grid-flow-dense' : ''}`} data-id="element-35">
          {/* Text content */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 30
        }} transition={{
          duration: 0.6
        }} className={`text-center lg:text-left ${reverse ? 'lg:col-start-2' : ''}`} data-id="element-36">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-full mb-8 shadow-lg" data-id="element-37">
              <Icon className="w-5 h-5" data-id="element-38" />
              <span className="font-semibold" data-id="element-39">{badge}</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{
            fontFamily: 'DM Serif Display, serif'
          }} data-id="element-40">
              {title} <span className="text-orange-500" data-id="element-41">{highlight}</span>
            </h2>

            <p className="text-xl text-gray-600 max-w-xl mx-auto lg:mx-0" data-id="element-42">
              {description}
            </p>
          </motion.div>

          {/* Phone mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.9
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className={reverse ? 'lg:col-start-1' : ''} data-id="element-43">
            <PhoneMockup imageUrl={imageUrl} alt={imageAlt} data-id="element-44" />
          </motion.div>
        </div>
      </div>
    </section>;
}